import React, { useEffect, useState, useCallback } from 'react';
import { Search, Plus, RotateCcw } from 'lucide-react';
import { journalApi } from '../api/journal';
import { coaApi } from '../api/coa';
import { contactsApi } from '../api/contacts';
import { purchaseApi } from '../api/purchase';
import { useToast } from '../hooks/useToast';

const colorMap: Record<string,string> = { blue:'badge-blue', green:'badge-green', amber:'badge-amber', gray:'badge-gray' };

const Journals: React.FC = () => {
  const { showToast } = useToast();
  const [entries, setEntries]   = useState<any[]>([]);
  const [total, setTotal]       = useState(0);
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [editId, setEditId] = useState<number|null>(null);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [form, setForm] = useState({ entry_date: new Date().toISOString().split('T')[0], branch_id:'', description:'' });
  const [lines, setLines] = useState([
    { account_id:'', contact_id:'', debit:'0', credit:'0', description:'' },
    { account_id:'', contact_id:'', debit:'0', credit:'0', description:'' },
  ]);

  const loadMeta = () => {
    coaApi.list().then(r => setAccounts(r.data.data));
    contactsApi.list().then(r => setContacts(r.data.data));
    purchaseApi.branches().then(r => setBranches(r.data.data));
  };

  const load = useCallback(() => {
    setLoading(true);
    journalApi.list({ search }).then(res => {
      setEntries(res.data.data); setTotal(res.data.total); setLoading(false);
    });
  }, [search]);

  const loadDetail = async (id: number) => {
    const res = await journalApi.get(id);
    setSelected(res.data.data);
  };

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const addLine    = () => setLines(l => [...l, { account_id:'', contact_id:'', debit:'0', credit:'0', description:'' }]);
  const removeLine = (i: number) => { if (lines.length <= 2) return; setLines(l => l.filter((_,idx) => idx !== i)); };
  const updateLine = (i: number, field: string, val: string) => setLines(l => l.map((line,idx) => idx===i ? {...line,[field]:val} : line));

  const totalDebit  = lines.reduce((s,l) => s + Number(l.debit), 0);
  const totalCredit = lines.reduce((s,l) => s + Number(l.credit), 0);
  const balanced    = Math.round(totalDebit*100) === Math.round(totalCredit*100) && totalDebit > 0;

  const handlePost = async () => {
    if (!balanced) { showToast(`Unbalanced - DR: ${totalDebit.toFixed(2)} CR: ${totalCredit.toFixed(2)}`, 'error'); return; }
    setSaving(true);
    try {
      const linesData = lines.filter(l => l.account_id).map(l => ({
        account_id:  Number(l.account_id),
        contact_id:  l.contact_id ? Number(l.contact_id) : null,
        debit:       Number(l.debit),
        credit:      Number(l.credit),
        description: l.description || null,
      }));
      const res = editId
        ? await journalApi.update(editId, { ...form, branch_id: form.branch_id ? Number(form.branch_id) : null, lines: linesData })
        : await journalApi.post({ ...form, branch_id: form.branch_id ? Number(form.branch_id) : null, lines: linesData });
      setEditId(null);
      showToast('Journal posted!');
      setShowForm(false); setEditId(null);
      setLines([
        { account_id:'', contact_id:'', debit:'0', credit:'0', description:'' },
        { account_id:'', contact_id:'', debit:'0', credit:'0', description:'' },
      ]);
      load();
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const openCorrect = (j: any) => {
    setForm({ entry_date: (j.entry_date || '').substring(0,10), branch_id: j.branch_id ? String(j.branch_id) : '', description: j.description || '' });
    setLines((j.lines || []).map((l: any) => ({ account_id: String(l.account_id), contact_id: l.contact_id ? String(l.contact_id) : '', debit: String(Number(l.debit) || 0), credit: String(Number(l.credit) || 0), description: l.description || '' })));
    setEditId(j.id);
    setSelected(null);
    setShowForm(true);
  };

  const handleReverse = async (id: number) => {
    if (!window.confirm('Reverse this journal entry? A mirror entry will be created.')) return;
    try {
      const res = await journalApi.reverse(id);
      showToast(res.data.message); setSelected(null); load();
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
  };

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Journal Entries</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}><Plus size={13} /> New Journal</button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header">
            <span>New Manual Journal</span>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <span style={{ fontSize:11, fontWeight:600, color: balanced ? 'var(--green)' : 'var(--red)' }}>
                DR: {totalDebit.toFixed(2)} | CR: {totalCredit.toFixed(2)} {balanced ? '✓' : '✗'}
              </span>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </div>
          <div className="card-body">
            <div className="form-grid-3" style={{ marginBottom:14 }}>
              <div className="form-group"><label className="form-label">Date *</label>
                <input className="form-control" type="date" value={form.entry_date} onChange={e => setForm(f=>({...f,entry_date:e.target.value}))} />
              </div>
              <div className="form-group"><label className="form-label">Branch</label>
                <select className="form-control" value={form.branch_id} onChange={e => setForm(f=>({...f,branch_id:e.target.value}))}>
                  <option value="">No branch</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Description</label>
                <input className="form-control" value={form.description} onChange={e => setForm(f=>({...f,description:e.target.value}))} placeholder="e.g. Depreciation adjustment" />
              </div>
            </div>

            <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:8 }}>
              <thead><tr style={{ background:'var(--sur2)' }}>
                <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Account</th>
                <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Contact</th>
                <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Debit</th>
                <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Credit</th>
                <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Note</th>
                <th></th>
              </tr></thead>
              <tbody>
                {lines.map((line, i) => (
                  <tr key={i} style={{ borderBottom:'1px solid var(--bdr)' }}>
                    <td style={{ padding:'6px 4px' }}>
                      <select className="form-control" value={line.account_id} onChange={e => updateLine(i,'account_id',e.target.value)} style={{ fontSize:12 }}>
                        <option value="">Select account</option>
                        {accounts.map(a => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}
                      </select>
                    </td>
                    <td style={{ padding:'6px 4px' }}>
                      <select className="form-control" value={line.contact_id} onChange={e => updateLine(i,'contact_id',e.target.value)} style={{ fontSize:12 }}>
                        <option value="">No contact</option>
                        {contacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </td>
                    <td style={{ padding:'6px 4px', width:110 }}><input className="form-control mono" type="number" value={line.debit} onChange={e => updateLine(i,'debit',e.target.value)} style={{ textAlign:'right' }} /></td>
                    <td style={{ padding:'6px 4px', width:110 }}><input className="form-control mono" type="number" value={line.credit} onChange={e => updateLine(i,'credit',e.target.value)} style={{ textAlign:'right' }} /></td>
                    <td style={{ padding:'6px 4px' }}><input className="form-control" value={line.description} onChange={e => updateLine(i,'description',e.target.value)} placeholder="Line note" style={{ fontSize:12 }} /></td>
                    <td style={{ padding:'6px 4px' }}><button onClick={() => removeLine(i)} style={{ background:'none', border:'none', color:'var(--red)', cursor:'pointer', fontSize:18 }}>×</button></td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:8 }}>
              <button className="btn btn-secondary btn-sm" onClick={addLine}>+ Add Line</button>
              <button className="btn btn-primary" onClick={handlePost} disabled={saving || !balanced}>{saving ? 'Posting...' : 'Post Journal'}</button>
            </div>
          </div>
        </div>
      )}

      {selected && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header">
            <span>Journal #{selected.id} - {selected.journal_type} - {selected.entry_date}</span>
            <div style={{ display:'flex', gap:8 }}>
              {!selected.is_reversal && !selected.reversed_by && (
                <button className="btn btn-secondary btn-sm" onClick={() => openCorrect(selected)}>Correct</button>
              )}
              {!selected.is_reversal && !selected.reversed_by && (
                <button className="btn btn-danger btn-sm" onClick={() => handleReverse(selected.id)}><RotateCcw size={12}/> Reverse</button>
              )}
              <button className="btn btn-secondary btn-sm" onClick={() => setSelected(null)}>Close</button>
            </div>
          </div>
          <div className="card-body" style={{ paddingBottom:0 }}>
            <div style={{ fontSize:12, color:'var(--tx3)', marginBottom:12 }}>{selected.description}</div>
          </div>
          <table>
            <thead><tr><th>Account</th><th>Contact</th><th style={{textAlign:'right'}}>Debit</th><th style={{textAlign:'right'}}>Credit</th><th>Note</th></tr></thead>
            <tbody>
              {selected.lines?.map((line: any, i: number) => (
                <tr key={i}>
                  <td className="td-code">{line.account_code} <span style={{fontFamily:'inherit',fontWeight:400,color:'var(--tx2)'}}>{line.account_name}</span></td>
                  <td style={{color:'var(--tx3)',fontSize:11}}>{line.contact_name || '-'}</td>
                  <td className="td-num td-green">{Number(line.debit) > 0 ? Number(line.debit).toFixed(2) : '-'}</td>
                  <td className="td-num td-red">{Number(line.credit) > 0 ? Number(line.credit).toFixed(2) : '-'}</td>
                  <td style={{color:'var(--tx3)',fontSize:11}}>{line.description || '-'}</td>
                </tr>
              ))}
              <tr style={{ background:'var(--sur2)', fontWeight:700 }}>
                <td colSpan={2} style={{ padding:'10px 14px', fontSize:12 }}>TOTAL</td>
                <td className="td-num">{Number(selected.total_debit).toFixed(2)}</td>
                <td className="td-num">{Number(selected.total_credit).toFixed(2)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="filter-bar">
        <div className="search-box"><Search size={13} color="var(--tx3)" /><input placeholder="Search journals..." value={search} onChange={e => setSearch(e.target.value)} /></div>
        <span className="record-count">{total} entries</span>
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr><th>#</th><th>Date</th><th>Type</th><th>Description</th><th style={{textAlign:'right'}}>Debit</th><th style={{textAlign:'right'}}>Credit</th><th>Status</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{textAlign:'center',padding:32,color:'var(--tx3)'}}>Loading...</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={7}><div className="empty-state"><p>No journal entries.</p></div></td></tr>
            ) : entries.map(e => (
              <tr key={e.id} onClick={() => loadDetail(e.id)} style={{cursor:'pointer'}}>
                <td className="td-code">#{e.id}</td>
                <td style={{color:'var(--tx3)',fontSize:11}}>{new Date(e.entry_date).toLocaleDateString()}</td>
                <td><span className="badge badge-blue">{e.journal_type}</span>{e.is_reversal ? <span className="badge badge-red" style={{marginLeft:4}}>Reversal</span> : ''}</td>
                <td style={{color:'var(--tx2)'}}>{e.description || '-'}</td>
                <td className="td-num">{Number(e.total_debit).toFixed(2)}</td>
                <td className="td-num">{Number(e.total_credit).toFixed(2)}</td>
                <td><span className={`badge ${colorMap[e.status_color]||'badge-gray'}`}>{e.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default Journals;
